/**
 * Zod schemas for gate-runner level content.
 *
 * Shape mirrors `@afr/sim` LevelDef / GateDef / EnemyWaveDef / PedestalDef
 * so validated JSON can be passed straight into `createRun({ level })`.
 *
 * Tone packages must not invent alternate ops or meta systems here —
 * reskin only (meshes, copy, VFX). Schema version bumps for rule changes.
 */
import { z } from "zod";
/** Content schema package identity (independent of sim SIM_API_VERSION). */
export const CONTENT_SCHEMA_VERSION = 1;
/** Gate arithmetic operators (GDD-SHARED). */
export const GateOpSchema = z.enum(["add", "mul", "sub", "div"]);
/**
 * Single gate on the path.
 *
 * - `z` — path-forward position where the gate resolves
 * - `op` / `k` — arithmetic applied to living unit count N
 * - `lane` — for binary pairs: negative = left, positive = right, 0 = center
 */
export const GateDefSchema = z.object({
    id: z.string().min(1),
    z: z.number().finite(),
    op: GateOpSchema,
    /** Operand K; non-negative integer after floor in sim. div uses max(1, K). */
    k: z.number().finite().nonnegative(),
    lane: z.number().finite().optional(),
});
/**
 * Enemy wave (aggregate Model B under the hood).
 * Presentation may show individual fodder; sim tracks HP/DPS pools.
 */
export const EnemyWaveDefSchema = z.object({
    id: z.string().min(1),
    z: z.number().finite(),
    /** Total HP pool for the wave. */
    hp: z.number().finite().positive(),
    /** Aggregate enemy DPS while in contact. */
    dps: z.number().finite().nonnegative(),
    /** Contact half-length along path (world units). */
    contactHalfLength: z.number().finite().positive().optional(),
});
/**
 * Pedestal transform trigger.
 * Prefer consume-all scaling (omit minN). If minN set, require N >= minN.
 */
export const PedestalDefSchema = z.object({
    id: z.string().min(1),
    z: z.number().finite(),
    minN: z.number().int().nonnegative().optional(),
});
/**
 * Full level document — data, not a hard-coded scene.
 *
 * Ad beat sheet levels 1–5 share this structure across all tones.
 * Forbidden in this schema: city-builder fields, calendar timers as gates,
 * alliance/meta progression.
 */
export const LevelDefSchema = z
    .object({
    /** Optional content-schema version stamp for authors / tooling. */
    schemaVersion: z.literal(CONTENT_SCHEMA_VERSION).optional(),
    id: z.string().min(1),
    name: z.string().min(1).optional(),
    /** Living friendly units at run start (typically 1–5 early chapters). */
    startN: z.number().int().nonnegative(),
    /** Path length in world units; run completes near this Z after content. */
    pathLength: z.number().finite().positive(),
    gates: z.array(GateDefSchema).optional(),
    waves: z.array(EnemyWaveDefSchema).optional(),
    pedestal: PedestalDefSchema.optional(),
    /** Optional climax horde after transform (ad moment on L5). */
    climaxWave: EnemyWaveDefSchema.optional(),
})
    .superRefine((level, ctx) => {
    // Pedestal must sit on the path.
    if (level.pedestal !== undefined && level.pedestal.z > level.pathLength) {
        ctx.addIssue({
            code: "custom",
            message: `pedestal.z (${level.pedestal.z}) exceeds pathLength (${level.pathLength})`,
            path: ["pedestal", "z"],
        });
    }
    // Gates should resolve before path end (soft authoring guard).
    for (let i = 0; i < (level.gates?.length ?? 0); i++) {
        const g = level.gates[i];
        if (g.z > level.pathLength) {
            ctx.addIssue({
                code: "custom",
                message: `gates[${i}].z (${g.z}) exceeds pathLength (${level.pathLength})`,
                path: ["gates", i, "z"],
            });
        }
    }
    // div operand must be meaningful (>= 1 when floored).
    for (let i = 0; i < (level.gates?.length ?? 0); i++) {
        const g = level.gates[i];
        if (g.op === "div" && Math.floor(g.k) < 1) {
            ctx.addIssue({
                code: "custom",
                message: `gates[${i}] div requires k >= 1`,
                path: ["gates", i, "k"],
            });
        }
    }
});
//# sourceMappingURL=schemas.js.map